import { useState } from 'react';
import dayjs from 'dayjs';
import { Box, Chip, InputAdornment, Popover, Stack, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import dateShortcuts from '~/enums/dateShortcuts';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export default function SingleCalendarWithShortcuts({
  dateValue,
  setDateValue,
  dateTempValue,
  setDateTempValue,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const showLabel = val => {
    if (!val || !val[0]) return '';
    const start = dayjs(val[0]).format('MMM D, YYYY');
    const end = val[1] ? dayjs(val[1]).format('MMM D, YYYY') : start;
    return `${start} - ${end}`;
  };

  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
    // preload temp with the current selected date range
    setDateTempValue(dateValue);
  };

  const handleClose = () => {
    // ✅ When closing (click away), commit if user picked a new valid range
    if (
      dateTempValue &&
      dateTempValue[0] &&
      dateTempValue[1] &&
      (!dateValue ||
        !dateValue[0].isSame(dateTempValue[0], 'day') ||
        !dateValue[1].isSame(dateTempValue[1], 'day'))
    ) {
      setDateValue(dateTempValue);
    }
    setAnchorEl(null);
  };

  const handleShortcut = getValue => {
    const v = getValue();
    setDateTempValue(v);
    setDateValue(v); // shortcuts commit immediately
    setAnchorEl(null);
  };

  // Allowed range
  const today = dayjs();
  const minDate = dayjs().subtract(90, 'day');
  const maxDate = today;

  const shouldDisableDate = date => date.isBefore(minDate, 'day') || date.isAfter(maxDate, 'day');

  // ✅ Prefer temp if valid, else current selection
  const displayValue =
    dateTempValue && dateTempValue[0] && dateTempValue[1] ? dateTempValue : dateValue;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <label htmlFor="single-calendar-with-shortcuts">Showing: </label>
        <TextField
          id="single-calendar-with-shortcuts"
          size="small"
          sx={{
            width: 250,
          }}
          value={showLabel(displayValue)}
          onClick={handleOpen}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ pl: 2 }}>
              <Stack sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1 }}>
                {dateShortcuts.map(s => (
                  <Chip
                    key={s.label}
                    label={s.label}
                    size="small"
                    onClick={() => handleShortcut(s.getValue)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Calendar */}
            <Box sx={{ p: 1.25, flex: 1 }}>
              <StaticDateRangePicker
                displayStaticWrapperAs="desktop"
                calendars={1}
                value={dateTempValue}
                onChange={newVal => setDateTempValue(newVal)}
                shouldDisableDate={shouldDisableDate}
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{
                  actionBar: { actions: [] },
                }}
              />
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
}
